import { useState, useEffect, RefObject } from 'react';

interface UseHorizontalScrollProps {
  tableScrollWrapper: RefObject<HTMLDivElement | null>;
  tableScrollerRef: RefObject<HTMLDivElement | null>;
  tableRef: RefObject<HTMLTableElement | null>;
  tableContainerRef: RefObject<HTMLDivElement | null>;
}

let timeout: NodeJS.Timeout;

export default function useHorizontalScroll({
  tableScrollWrapper,
  tableRef,
  tableContainerRef,
  tableScrollerRef,
}: UseHorizontalScrollProps) {
  const [scrollBarThumbWidth, setScrollBarThumbWidth] = useState(0);
  const [accessibleScrollWidth, setAccessibleScrollWidth] = useState(0);
  const [ratio, setRatio] = useState(0);
  const [scrollThumbPosition, setScrollThumbPosition] = useState(0);

  const onScroll = (scroll: number, thumbPosition: number) => {
    if (tableContainerRef?.current) {
      tableContainerRef.current.scroll({
        left: scroll,
        behavior: 'smooth',
      });

      timeout = setTimeout(() => {
        setScrollThumbPosition(thumbPosition);
        clearTimeout(timeout);
      }, 300);
    }
  };

  const onScrollButton = (direction: 'left' | 'right') => {
    clearTimeout(timeout);

    if (tableContainerRef?.current) {
      let scroll = tableContainerRef.current.scrollLeft;

      if (direction === 'left' && scroll + 50 > accessibleScrollWidth) {
        scroll = accessibleScrollWidth;
      } else if (direction === 'left') {
        scroll += 50;
      } else if (direction === 'right' && scroll >= 50) {
        scroll -= 50;
      } else if (direction === 'right' && scroll <= 49) {
        scroll = 0;
      }

      onScroll(scroll, -(scroll * ratio));
    }
  };

  const onWheel = (x: number) => {
    clearTimeout(timeout);

    if (tableScrollerRef?.current) {
      // eslint-disable-next-line
      tableScrollerRef.current.style.transform = `translate(${
        x * ratio
      }px, 0px)`;
    }

    timeout = setTimeout(() => {
      setScrollThumbPosition(-(x * ratio));
      clearTimeout(timeout);
    }, 300);
  };

  const onDrag = (x: number) => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scroll({ left: x / ratio });
    }
  };

  const onDragEnd = (x: number) => {
    if (tableContainerRef.current) {
      setScrollThumbPosition(-x);
    }
  };
  // TODO: баг, tableRef.current.offsetWidth - не обновляется в юзэффете последний раз BSL-1089
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (tableScrollWrapper.current && tableRef.current?.offsetWidth) {
        const viewportWidth = tableScrollWrapper.current.offsetWidth;
        const { scrollWidth } = tableRef.current;
        const contentWidth = tableRef.current.offsetWidth;

        const scrollBarArea = viewportWidth + (40 + 32 + 60);

        const viewableRatio = viewportWidth / contentWidth;

        setRatio(viewableRatio);
        setAccessibleScrollWidth(scrollWidth - scrollBarArea);
        setScrollBarThumbWidth(scrollBarArea * viewableRatio);
      }
    });

    if (tableScrollWrapper && tableScrollWrapper.current) {
      resizeObserver.observe(tableScrollWrapper.current);
    }

    return () => resizeObserver.disconnect();
    // eslint-disable-next-line react-hooks/refs
  }, [tableRef, tableRef.current?.offsetWidth, tableScrollWrapper]);

  return {
    onScrollButton,
    onWheel,
    onDrag,
    onDragEnd,
    scrollBarThumbWidth,
    scrollThumbPosition,
    accessibleDragPosition: accessibleScrollWidth * ratio,
  };
}
